import { Users, Crown, UserPlus, MoreHorizontal, UserMinus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import GlassPill from "@/components/ui/GlassPill";
import MobileSidebarAccordion from "@/components/ui/MobileSidebarAccordion";
import SidebarHeader from "@/components/ui/SidebarHeader";
import { useIsMobile } from "@/hooks/layout/use-mobile";
import { useGlobalModals } from "@/contexts/GlobalModalsContext";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "@/hooks/auth/use-user";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/errors/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface TeamMembersSidebarProps {
  workbookId?: string;
}

interface WorkbookEditor {
  id: string;
  first_name: string;
  username: string;
  profile_picture_url?: string;
  display_name: string;
  is_owner: boolean;
}

interface WorkbookData {
  id: string;
  title: string;
  user_id: string;
  owner_first_name: string;
  owner_username: string;
  owner_profile_picture_url?: string;
  owner_display_name: string;
}

export const TeamMembersSidebar: React.FC<TeamMembersSidebarProps> = ({ workbookId: propWorkbookId }) => {
  const isMobile = useIsMobile();
  const { openInviteMembersModal } = useGlobalModals();
  const { id } = useParams<{ id: string }>(); // Use correct parameter name
  const { user } = useUser();
  const navigate = useNavigate();
  
  // Use prop if provided, otherwise fall back to URL parameter
  const workbookId = propWorkbookId || id;
  
  // State management
  const [editors, setEditors] = useState<WorkbookEditor[]>([]);
  const [workbookData, setWorkbookData] = useState<WorkbookData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [canManageEditors, setCanManageEditors] = useState(false);

  // Fetch editors and workbook data
  const fetchEditorsAndWorkbook = async () => {
    if (!workbookId) return;

    console.log('👥 EDITORS DEBUG: Fetching editors for workbook', { workbookId });
    setIsLoading(true);

    try {
      // Get editors
      const { data: editorsData, error: editorsError } = await supabase.rpc('workbook_collaboration', {
        p_action: 'get_editors',
        p_workbook_id: workbookId
      });

      if (editorsError) {
        console.error('❌ EDITORS ERROR: Failed to fetch editors', editorsError);
        toast({
          title: "Failed to load team members",
          description: "Please refresh the page to try again.",
          variant: "destructive"
        });
        return;
      }

      console.log('✅ EDITORS SUCCESS: Editors fetched', { editorsData });
      setEditors(editorsData || []);

      // Check if current user can manage editors (is owner)
      const isOwner = editorsData?.some((editor: WorkbookEditor) => 
        editor.id === user?.id && editor.is_owner
      );
      setCanManageEditors(isOwner || false);

      // Extract workbook data from editors response (owner info)
      const ownerData = editorsData?.find((editor: WorkbookEditor) => editor.is_owner);
      if (ownerData) {
        setWorkbookData({
          id: workbookId,
          title: 'Current Workbook', // We don't have title in editors response
          user_id: ownerData.id,
          owner_first_name: ownerData.first_name,
          owner_username: ownerData.username,
          owner_profile_picture_url: ownerData.profile_picture_url,
          owner_display_name: ownerData.display_name
        });
      }

    } catch (error) {
      console.error('❌ EDITORS ERROR: Unexpected error', error);
      toast({
        title: "Error loading team members",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Remove editor function
  const handleRemoveEditor = async (editorId: string) => {
    if (!workbookId || (!canManageEditors && editorId !== user?.id)) return;

    setIsRemoving(editorId);
    console.log('🗑️ REMOVE DEBUG: Removing editor', { editorId, workbookId });

    try {
      const { data, error } = await supabase.rpc('workbook_collaboration', {
        p_action: 'remove_editor',
        p_workbook_id: workbookId,
        p_user_id: editorId
      });

      if (error) {
        console.error('❌ REMOVE ERROR: Failed to remove editor', error);
        toast({
          title: editorId === user?.id ? "Failed to leave workbook" : "Failed to remove member",
          description: "Please try again.",
          variant: "destructive"
        });
        return;
      }

      console.log('✅ REMOVE SUCCESS: Editor removed', { editorId, response: data });
      
      // If user removed themselves, redirect to project manager
      if (editorId === user?.id) {
        toast({
          title: "Left workbook",
          description: "You have successfully left the workbook.",
          variant: "default"
        });
        
        // Navigate to project manager page
        navigate('/project-manager');
        return;
      }
      
      // Otherwise refresh editors list for other removals
      await fetchEditorsAndWorkbook();
      
      toast({
        title: "Member removed",
        description: "The member has been removed from the workbook.",
        variant: "default"
      });

    } catch (error) {
      console.error('❌ REMOVE ERROR: Unexpected error', error);
      toast({
        title: "Error removing member",
        description: "An unexpected error occurred.",
        variant: "destructive"
      });
    } finally {
      setIsRemoving(null);
    }
  };

  // Fetch data on component mount and when workbookId changes
  useEffect(() => {
    fetchEditorsAndWorkbook();
  }, [workbookId]);

  const handleInviteMembers = () => {
    if (workbookId) {
      // Pass refresh callback to be called when members are successfully added
      openInviteMembersModal(workbookId, () => {
        console.log('🔄 REFRESH DEBUG: Refreshing editors after successful invitation');
        fetchEditorsAndWorkbook();
      });
    }
  };

  return (
    <MobileSidebarAccordion
      variant="purple"
      persistKey="team-members-sidebar"
      defaultExpanded={!isMobile}
      className="h-full"
    >
      <SidebarHeader 
        title="Team Members" 
        icon={Users} 
        variant="purple" 
      />
      
      <div className="space-y-6">
        {/* Invite Button */}
        <div className="space-y-4">
          <Button 
            className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white border-0" 
            onClick={handleInviteMembers}
            disabled={!workbookId || !canManageEditors}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Members
          </Button>
          {!canManageEditors && (
            <p className="text-xs text-muted-foreground text-center">
              Only the workbook owner can invite members
            </p>
          )}
        </div>

        {/* Project Team */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-purple-600 dark:text-purple-400">Project Team</h4>
            <GlassPill 
              type={isLoading ? '...' : `${editors.length} member${editors.length !== 1 ? 's' : ''}`}
              variant="default"
              className="text-xs"
            />
          </div>
          
          {isLoading ? (
            <div className="text-center py-6 text-muted-foreground">
              <div className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm">Loading team members...</p>
            </div>
          ) : editors.length > 0 ? (
            <div className="space-y-3">
              {editors.map((editor) => (
                <div key={editor.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={editor.profile_picture_url} />
                    <AvatarFallback className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-600 dark:text-purple-400">
                      {editor.first_name?.[0]?.toUpperCase() || editor.username?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                                              {editor.display_name || editor.first_name || 'Anonymous'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {editor.username ? `@${editor.username}` : editor.first_name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <GlassPill 
                      type={editor.is_owner ? 'Owner' : 'Editor'}
                      variant={editor.is_owner ? 'slate' : 'gray'}
                      className="text-xs"
                      icon={editor.is_owner ? <Crown className="h-3 w-3" /> : undefined}
                      iconPosition="left"
                    />
                    {/* Remove editor dropdown - for owners to remove editors, or editors to remove themselves */}
                    {!editor.is_owner && (canManageEditors || editor.id === user?.id) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 hover:bg-gradient-to-r hover:from-purple-500/20 hover:to-blue-500/20 hover:text-white transition-all duration-200"
                            disabled={isRemoving === editor.id}
                          >
                            {isRemoving === editor.id ? (
                              <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full" />
                            ) : (
                              <MoreHorizontal className="h-3 w-3" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem 
                            onClick={() => handleRemoveEditor(editor.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            {editor.id === user?.id ? 'Leave workbook' : 'Remove from workbook'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No team members yet</p>
              <p className="text-xs opacity-75">Invite colleagues to collaborate</p>
            </div>
          )}
        </div>
      </div>
    </MobileSidebarAccordion>
  );
}; 